import { InputTextarea } from "primereact/inputtextarea";
import styled from "styled-components";
import { IOpenAiChatModelConfig } from "types/Chat";

/** Matches MAX_SUGGESTED_PROMPTS in the API's chat.dto.ts. */
export const MAX_SUGGESTED_PROMPTS = 6;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
`;

/**
 * CLE-2811 — the patient widget deliberately ignores `config.suggestedPrompts` and shows two
 * fixed questions (see components/ChatWidget/chatWidget.helpers.ts). An editable textarea that
 * changes nothing is a trap for an admin, so the patient bot gets a read-only note instead.
 */
export function modelTypeSupportsSuggestedPrompts(modelType?: string): boolean {
  return modelType !== "PatientChatBot";
}

/**
 * Strips `suggestedPrompts` from a config that is about to be saved on a model type which
 * cannot show them — e.g. questions typed for a Contract Management Bot and then switched
 * (or copied) to a Patient Chat Bot. Returns the config untouched for every other type.
 */
export function prepareModelConfig(
  modelType: string | undefined,
  config?: IOpenAiChatModelConfig
): IOpenAiChatModelConfig | undefined {
  if (!config || modelTypeSupportsSuggestedPrompts(modelType)) {
    return config;
  }

  const stripped = { ...config };
  delete stripped.suggestedPrompts;
  return stripped;
}

interface ISuggestedQuestionsFieldProps {
  modelType?: string;
  value: string;
  onChange: (text: string) => void;
}

export function SuggestedQuestionsField({
  modelType,
  value,
  onChange,
}: ISuggestedQuestionsFieldProps) {
  if (!modelTypeSupportsSuggestedPrompts(modelType)) {
    return (
      <FormField>
        <small className="text-muted">
          The patient chatbot&apos;s suggested questions are fixed and cannot be
          changed here.
        </small>
      </FormField>
    );
  }

  return (
    <FormField>
      <Label htmlFor="suggestedPrompts">Suggested Questions</Label>
      <InputTextarea
        id="suggestedPrompts"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        className="w-full"
        placeholder={
          "When was this contract signed?\nWhat is the timely filing window?"
        }
      />
      <small className="text-muted">
        One question per line, up to {MAX_SUGGESTED_PROMPTS}. These are the
        clickable suggestions shown in the chat widget before the first message.
        Leave this empty to keep the widget&apos;s default questions. A page
        that sets its own questions (such as a single contract) overrides these.
      </small>
    </FormField>
  );
}
